import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../constants';
import { fetchWrapper } from '../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IPayPeriods, ITimesheet } from '../utils/types';

const name = 'timesheets';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<TimesheetState>) => createExtraReducers(builder),
});

export interface TimesheetState {
  list: Array<ITimesheet>;
  serviceCodes: any[];
  selectedPayPeriod: string[];
  payPeriods: IPayPeriods;
  removeNewTimesheet: boolean;
  error: any;
  loading: boolean;
  isAddingTimesheet: boolean;
  editingTimesheetId: number | null;
  isPayPeriodEditable: boolean;
}

function createInitialState(): TimesheetState {
  return {
    list: [],
    serviceCodes: [],
    selectedPayPeriod: [],
    payPeriods: {
      present: [],
      past: [],
      future: [],
    },
    removeNewTimesheet: false,
    loading: false,
    error: null,
    isAddingTimesheet: false,
    editingTimesheetId: null,
    isPayPeriodEditable: false,
  };
}

function createReducers() {
  return {
    updateState: updateState,
  };

  function updateState(state: TimesheetState, action: PayloadAction<TimesheetUpdatePayload>) {
    Object.assign(state, { [action.payload.key]: action.payload.value });
  }
}

type TimesheetUpdatePayload = {
  [Key in keyof TimesheetState]: { key: Key; value: TimesheetState[Key] };
}[keyof TimesheetState];

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/timesheets`;

  return {
    getList: getList(),
    create: create(),
    update: update(),
    remove: remove(),
    getPayPeriods: getPayPeriods(),
  };

  function getList() {
    return createAsyncThunk(`${name}/getList`, async ({ params, getAccessTokenSilently }: any) => {
      const response = await fetchWrapper.get({
        url: `${baseUrl}/?start=${params.start}&end=${params.end}&utc_offset=${params.utc_offset}`,
        getAccessTokenSilently,
      });
      return response;
    });
  }

  function create() {
    return createAsyncThunk(`${name}/create`, async ({ data, getAccessTokenSilently }: any) => {
      const response = await fetchWrapper.post(
        { url: `${baseUrl}/`, getAccessTokenSilently },
        data,
      );
      return response;
    });
  }

  function update() {
    return createAsyncThunk(`${name}/update`, async ({ data, getAccessTokenSilently }: any) => {
      const response = await fetchWrapper.patch(
        { url: `${baseUrl}/${data.id}/`, getAccessTokenSilently },
        data,
      );
      return response;
    });
  }

  function remove() {
    return createAsyncThunk(`${name}/remove`, async ({ id, getAccessTokenSilently }: any) => {
      const response = await fetchWrapper.delete({
        url: `${baseUrl}/${id}/`,
        getAccessTokenSilently,
      });
      return response;
    });
  }

  function getPayPeriods() {
    return createAsyncThunk(
      `${name}/getPayPeriods`,
      async ({ params, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/pay_periods/?start=${params.start}&end=${params.end}&utc_offset=${params.utc_offset}`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<TimesheetState>) {
  const { getList, create, update, remove, getPayPeriods } = extraActions;

  builder
    // getList()
    .addCase(getList.pending, (state: TimesheetState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getList.fulfilled, (state: TimesheetState, action: PayloadAction<ITimesheet[]>) => {
      state.list = action.payload;
      state.loading = false;
    })
    .addCase(getList.rejected, (state: TimesheetState, action: any) => {
      state.list = [];
      state.error = action.error;
      state.loading = false;
    })

    // create()
    .addCase(create.pending, (state: TimesheetState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(create.fulfilled, (state: TimesheetState, action: PayloadAction<ITimesheet>) => {
      state.list = [...state.list, action.payload];
      state.loading = false;
      const toastId = 'timesheet-created';
      toast.success('Timesheet created successfully', { toastId });
    })
    .addCase(create.rejected, (state: TimesheetState, action: any) => {
      state.error = action.error;
      toast.error(action.error.message);

      state.loading = false;
    })

    // update()
    .addCase(update.pending, (state: TimesheetState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(update.fulfilled, (state: TimesheetState, action: PayloadAction<ITimesheet>) => {
      state.list = state.list.map((timesheet) =>
        timesheet?.id === action.payload.id ? action.payload : timesheet,
      );
      state.loading = false;
    })
    .addCase(update.rejected, (state: TimesheetState, action: any) => {
      state.error = action.error;
      toast.error(action.error.message);

      state.loading = false;
    })

    // remove()
    .addCase(remove.pending, (state: TimesheetState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      remove.fulfilled,
      (
        state: TimesheetState,
        action: PayloadAction<ITimesheet, string, { arg: { id: number } }>,
      ) => {
        state.list = state.list.filter((timesheet) => timesheet?.id !== action.meta.arg.id);
        state.loading = false;
        const toastId = 'timesheet-delete';
        toast.success('Timesheet deleted successfully', { toastId });
      },
    )
    .addCase(remove.rejected, (state: TimesheetState, action: any) => {
      state.error = action.error;
      toast.error(action.error.message);
      state.loading = false;
    })

    // getPayPeriods()
    .addCase(getPayPeriods.pending, (state: TimesheetState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getPayPeriods.fulfilled,
      (state: TimesheetState, action: PayloadAction<IPayPeriods>) => {
        state.payPeriods = action.payload;
        state.loading = false;
      },
    )
    .addCase(getPayPeriods.rejected, (state: TimesheetState, action: any) => {
      state.list = [];
      state.error = action.error;
      state.loading = false;
    });
}

export const timesheetActions = { ...slice.actions, ...extraActions };
export const timesheetReducer = slice.reducer;
